import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  TextField,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Alert,
  CircularProgress,
  Backdrop,
} from "@mui/material";
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import Sidebar from "../components/Sidebar";
import Navbar from "../components/Navbar";
import SectionCard from "../components/UserGuides/SectionCard";

const API_BASE_URL =
  process.env.REACT_APP_SUPERADMIN_APP_API_BASE_URL || "http://localhost:4009";

const UserGuideForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;

  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [publishDialog, setPublishDialog] = useState(false);
  const [unsavedChanges, setUnsavedChanges] = useState(false);

  const [formData, setFormData] = useState({
    navigation_name: "",
    title: "",
    sections: [],
  });

  const [draggedSectionIndex, setDraggedSectionIndex] = useState(null);

  const showSnackbar = useCallback((message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const fetchUserGuide = useCallback(async () => {
    if (!id) return;

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/user-guides/${id}`);
      const data = await response.json();
      if (data.success) {
        setFormData({
          navigation_name: data.data.navigation_name || "",
          title: data.data.title || "",
          sections: data.data.sections || [],
        });
      } else {
        showSnackbar(data.message || "Failed to fetch user guide", "error");
        navigate("/UserGuides");
      }
    } catch (error) {
      showSnackbar("Failed to fetch user guide", "error");
      navigate("/UserGuides");
    } finally {
      setLoading(false);
    }
  }, [id, navigate, showSnackbar]);

  useEffect(() => {
    if (isEditMode && id) {
      fetchUserGuide();
    }
  }, [id, isEditMode, fetchUserGuide]);

  // Warn user if they try to navigate away with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (unsavedChanges) {
        e.preventDefault();
        e.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [unsavedChanges]);

  const handleSidebarStateChange = (isOpen) => {
    setSidebarOpen(isOpen);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ open: false, message: "", severity: "success" });
  };

  const handleFieldChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    setUnsavedChanges(true);
  };

  const handleAddSection = () => {
    const newSection = {
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      heading: "",
      heading_level: "H2",
      typography: "Roboto Bold 24px",
      text_html: "",
      attachments: [],
    };
    setFormData((prev) => ({
      ...prev,
      sections: [...prev.sections, newSection],
    }));
    setUnsavedChanges(true);
  };

  const handleSectionUpdate = (index, updatedSection) => {
    const newSections = [...formData.sections];
    newSections[index] = updatedSection;
    setFormData((prev) => ({
      ...prev,
      sections: newSections,
    }));
    setUnsavedChanges(true);
  };

  const handleSectionDelete = (index) => {
    if (window.confirm("Are you sure you want to delete this section?")) {
      const newSections = formData.sections.filter((_, i) => i !== index);
      setFormData((prev) => ({
        ...prev,
        sections: newSections,
      }));
      setUnsavedChanges(true);
    }
  };

  const handleSectionDuplicate = (index) => {
    const sectionToDuplicate = formData.sections[index];
    const duplicatedSection = {
      ...sectionToDuplicate,
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      heading: `${sectionToDuplicate.heading} (Copy)`,
      attachments:
        sectionToDuplicate.attachments?.map((att) => ({
          ...att,
          id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        })) || [],
    };
    const newSections = [...formData.sections];
    newSections.splice(index + 1, 0, duplicatedSection);
    setFormData((prev) => ({
      ...prev,
      sections: newSections,
    }));
    setUnsavedChanges(true);
  };

  const handleSectionMoveUp = (index) => {
    if (index === 0) return;
    const newSections = [...formData.sections];
    [newSections[index - 1], newSections[index]] = [
      newSections[index],
      newSections[index - 1],
    ];
    setFormData((prev) => ({
      ...prev,
      sections: newSections,
    }));
    setUnsavedChanges(true);
  };

  const handleSectionMoveDown = (index) => {
    if (index === formData.sections.length - 1) return;
    const newSections = [...formData.sections];
    [newSections[index], newSections[index + 1]] = [
      newSections[index + 1],
      newSections[index],
    ];
    setFormData((prev) => ({
      ...prev,
      sections: newSections,
    }));
    setUnsavedChanges(true);
  };

  const handleSectionDragStart = (e, index) => {
    setDraggedSectionIndex(index);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleSectionDragOver = (e, index) => {
    e.preventDefault();
    if (draggedSectionIndex === null || draggedSectionIndex === index) {
      return;
    }

    const newSections = [...formData.sections];
    const draggedItem = newSections[draggedSectionIndex];
    newSections.splice(draggedSectionIndex, 1);
    newSections.splice(index, 0, draggedItem);
    setDraggedSectionIndex(index);
    setFormData((prev) => ({
      ...prev,
      sections: newSections,
    }));
    setUnsavedChanges(true);
  };

  const handleSectionDrop = (e, index) => {
    e.preventDefault();
    setDraggedSectionIndex(null);
  };

  const validateForm = () => {
    // Validate navigation name
    if (!formData.navigation_name || formData.navigation_name.trim() === "") {
      showSnackbar("Navigation name is required", "error");
      return false;
    }

    // Validate title
    if (!formData.title || formData.title.trim() === "") {
      showSnackbar("Title is required", "error");
      return false;
    }

    // Validate sections
    if (formData.sections.length === 0) {
      showSnackbar(
        "At least one section is required before publishing",
        "error"
      );
      return false;
    }

    // Validate each section
    for (let i = 0; i < formData.sections.length; i++) {
      const section = formData.sections[i];

      // Heading is optional, but if provided, heading_level must be valid
      if (section.heading && section.heading.trim() !== "") {
        if (
          !section.heading_level ||
          !["H2", "H3", "H4", "H5", "H6"].includes(section.heading_level)
        ) {
          showSnackbar(
            `Section ${
              i + 1
            } has an invalid heading level. Please select a valid heading level.`,
            "error"
          );
          return false;
        }
      }

      if (!section.typography || section.typography.trim() === "") {
        showSnackbar(
          `Section ${
            i + 1
          } typography is required. Please select a typography style.`,
          "error"
        );
        return false;
      }
    }

    return true;
  };

  const handlePublish = () => {
    if (!validateForm()) {
      return;
    }
    setPublishDialog(true);
  };

  const handlePublishConfirm = async () => {
    setPublishDialog(false);
    setSaving(true);

    // Save current form state in case of failure
    const savedFormData = JSON.parse(JSON.stringify(formData));

    try {
      const url = isEditMode
        ? `${API_BASE_URL}/api/user-guides/${id}`
        : `${API_BASE_URL}/api/user-guides`;
      const method = isEditMode ? "PATCH" : "POST";

      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      if (data.success) {
        showSnackbar(
          isEditMode
            ? "User guide updated successfully"
            : "User guide created successfully",
          "success"
        );
        setUnsavedChanges(false);
        setTimeout(() => {
          navigate("/UserGuides");
        }, 1500);
      } else {
        // Restore form state on failure
        setFormData(savedFormData);
        showSnackbar(
          data.message || "Failed to save user guide. Please try again.",
          "error"
        );
      }
    } catch (error) {
      // Restore form state on failure
      setFormData(savedFormData);
      const errorMessage =
        error.message ||
        "Network error. Please check your connection and try again.";
      showSnackbar(errorMessage, "error");
      console.error("Publish error:", error);
    } finally {
      setSaving(false);
    }
  };

  const handlePublishCancel = () => {
    setPublishDialog(false);
  };

  const handleBack = () => {
    if (unsavedChanges) {
      const confirmed = window.confirm(
        "You have unsaved changes. Are you sure you want to leave? Your changes will be lost."
      );
      if (confirmed) {
        navigate("/UserGuides");
      }
    } else {
      navigate("/UserGuides");
    }
  };

  // Handle browser back button with unsaved changes warning
  useEffect(() => {
    const handlePopState = (e) => {
      if (unsavedChanges) {
        const confirmed = window.confirm(
          "You have unsaved changes. Are you sure you want to leave? Your changes will be lost."
        );
        if (!confirmed) {
          window.history.pushState(null, "", window.location.href);
        }
      }
    };

    window.history.pushState(null, "", window.location.href);
    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [unsavedChanges]);

  if (loading) {
    return (
      <div>
        <Sidebar onSidebarStateChange={handleSidebarStateChange} />
        <Navbar sidebarOpen={sidebarOpen} />
        <Box
          sx={{
            ml: sidebarOpen ? "220px" : "100px",
            p: 4,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "100vh",
          }}
        >
          <CircularProgress sx={{ color: "#9048FB" }} />
        </Box>
      </div>
    );
  }

  return (
    <div>
      <Sidebar onSidebarStateChange={handleSidebarStateChange} />
      <Navbar sidebarOpen={sidebarOpen} />

      <Box
        sx={{
          ml: sidebarOpen ? "220px" : "100px",
          p: { xs: 2, sm: 3, md: 4, xl: 4 },
          backgroundColor: "#fafafa",
          minHeight: "100vh",
          transition: "margin-left 0.3s ease",
          fontFamily: "Outfit",
        }}
      >
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={handleBack}
                sx={{
                  color: "#6b7280",
                  textTransform: "none",
                  fontFamily: "Outfit",
                  "&:hover": {
                    backgroundColor: "#f3f4f6",
                  },
                }}
              >
                Back
              </Button>
              <h1
                style={{
                  fontSize: "18px",
                  fontWeight: 700,
                  color: "#2B3674",
                  margin: 0,
                  fontFamily: "Outfit",
                }}
              >
                {isEditMode ? "Edit User Guide" : "Create New User Guide"}
              </h1>
            </Box>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
              onClick={handlePublish}
              disabled={saving}
              sx={{
                backgroundColor: "#9048FB",
                color: "#ffffff",
                textTransform: "none",
                fontFamily: "Outfit",
                fontWeight: 600,
                borderRadius: "8px",
                px: 3,
                py: 1.5,
                "&:hover": {
                  backgroundColor: "#7c3aed",
                },
              }}
            >
              {saving ? "Publishing..." : "Publish"}
            </Button>
          </Box>
        </Box>

        {/* Form */}
        <Box
          sx={{
            backgroundColor: "#ffffff",
            borderRadius: "16px",
            padding: { xs: "16px", sm: "24px", md: "32px", xl: "40px" },
            boxShadow:
              "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
            border: "1px solid #f1f5f9",
            position: "relative",
          }}
        >
          {/* Saving Overlay */}
          {saving && (
            <Backdrop
              open={saving}
              sx={{
                position: "absolute",
                zIndex: 1000,
                backgroundColor: "rgba(255, 255, 255, 0.8)",
                borderRadius: "16px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
              }}
            >
              <CircularProgress sx={{ color: "#9048FB" }} />
              <Typography
                sx={{
                  fontFamily: "Outfit",
                  fontWeight: 600,
                  color: "#374151",
                }}
              >
                Publishing guide...
              </Typography>
            </Backdrop>
          )}
          {/* Basic Fields */}
          <Box sx={{ mb: 4 }}>
            <TextField
              label="Navigation Name"
              value={formData.navigation_name}
              onChange={(e) =>
                handleFieldChange("navigation_name", e.target.value)
              }
              required
              fullWidth
              sx={{
                mb: 2,
                "& .MuiOutlinedInput-root": {
                  fontFamily: "Outfit",
                },
                "& .MuiInputLabel-root": {
                  fontFamily: "Outfit",
                },
              }}
            />
            <TextField
              label="Title"
              value={formData.title}
              onChange={(e) => handleFieldChange("title", e.target.value)}
              required
              fullWidth
              sx={{
                "& .MuiOutlinedInput-root": {
                  fontFamily: "Outfit",
                },
                "& .MuiInputLabel-root": {
                  fontFamily: "Outfit",
                },
              }}
            />
          </Box>

          {/* Sections */}
          <Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography
                sx={{
                  fontFamily: "Outfit",
                  fontWeight: 600,
                  color: "#374151",
                  fontSize: "16px",
                }}
              >
                Sections
              </Typography>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleAddSection}
                sx={{
                  borderColor: "#9048FB",
                  color: "#9048FB",
                  textTransform: "none",
                  fontFamily: "Outfit",
                  fontWeight: 600,
                  borderRadius: "8px",
                  "&:hover": {
                    borderColor: "#7c3aed",
                    backgroundColor: "#f3e8ff",
                  },
                }}
              >
                Add Section
              </Button>
            </Box>

            {formData.sections.length === 0 ? (
              <Box
                sx={{
                  textAlign: "center",
                  py: 4,
                  border: "2px dashed #e5e7eb",
                  borderRadius: "12px",
                  backgroundColor: "#fafafa",
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "Outfit",
                    color: "#6b7280",
                    mb: 2,
                  }}
                >
                  No sections yet. Click "Add Section" to get started.
                </Typography>
              </Box>
            ) : (
              formData.sections.map((section, index) => (
                <SectionCard
                  key={section.id}
                  section={section}
                  index={index}
                  onUpdate={(updatedSection) =>
                    handleSectionUpdate(index, updatedSection)
                  }
                  onDelete={() => handleSectionDelete(index)}
                  onDuplicate={() => handleSectionDuplicate(index)}
                  onMoveUp={() => handleSectionMoveUp(index)}
                  onMoveDown={() => handleSectionMoveDown(index)}
                  onDragStart={(e) => handleSectionDragStart(e, index)}
                  onDragOver={(e) => handleSectionDragOver(e, index)}
                  onDrop={(e) => handleSectionDrop(e, index)}
                  isDragging={draggedSectionIndex === index}
                  currentGuideId={id}
                />
              ))
            )}
          </Box>
        </Box>
      </Box>

      {/* Publish Confirmation Dialog */}
      <Dialog
        open={publishDialog}
        onClose={handlePublishCancel}
        sx={{
          "& .MuiDialog-paper": {
            borderRadius: "12px",
            fontFamily: "Outfit",
          },
        }}
      >
        <DialogTitle sx={{ fontFamily: "Outfit", fontWeight: 600 }}>
          Publish User Guide
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontFamily: "Outfit" }}>
            Are you sure you want to publish this guide? This will make it
            available to users.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handlePublishCancel}
            sx={{
              fontFamily: "Outfit",
              textTransform: "none",
              color: "#6b7280",
            }}
          >
            Back to Editing
          </Button>
          <Button
            onClick={handlePublishConfirm}
            variant="contained"
            sx={{
              fontFamily: "Outfit",
              textTransform: "none",
              backgroundColor: "#9048FB",
              "&:hover": {
                backgroundColor: "#7c3aed",
              },
            }}
          >
            Yes, Publish
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{
            borderRadius: "12px",
            fontFamily: "Outfit",
            fontWeight: 500,
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default UserGuideForm;
